/**
 * Static Input Components
 * 
 * Moderne UX/UI-Komponenten für verschiedene statische Eingabetypen:
 * - Text
 * - Datum + optionale Uhrzeit
 * - Ganzzahl
 * - Kommazahl
 * - Farbe
 */

import React, { useMemo, useState } from 'react';
import { Input } from '@/ui-components/ui/input';
import { Button } from '@/ui-components/ui/button';
import { Label } from '@/ui-components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui-components/ui/popover';
import { Calendar } from '@/ui-components/ui/calendar';
import { Switch } from '@/ui-components/ui/switch';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Hash, 
  Type,
  Image as ImageIcon,
  X,
  Palette,
  Check
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { MediaType } from '@/page-components/post-generator/core/template.schemas';
import { AddImageDialog } from '@/page-components/post-generator/components/AddImageDialog';
import { useOrganizationsCustomerFindOneQuery } from '@/lib/api/requests/organizations.requests';
import { usePermissions } from '@/lib/hooks/use-permissions';

interface StaticInputProps {
  value: any;
  onChange: (value: any, meta?: { width?: number; height?: number }) => void;
  mediaType: MediaType;
  className?: string;
  allowedMimeTypes?: string[];
}

/**
 * Text Input Component
 */
export const TextInput: React.FC<StaticInputProps> = ({ value, onChange, className }) => {
  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <Type className="w-3 h-3 text-gray-400" />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Text eingeben..."
          className="h-6 text-xs flex-1"
        />
      </div>
    </div>
  );
};

/**
 * Date Input Component with optional time
 */
export const DateTimeInput: React.FC<StaticInputProps> = ({ value, onChange, className }) => {
  // Sicheres Parsen des Eingabewerts
  const parseInputValue = (inputValue: any): Date | undefined => {
    if (!inputValue) return undefined;
    
    try {
      const date = new Date(inputValue);
      return isNaN(date.getTime()) ? undefined : date;
    } catch {
      return undefined;
    }
  };

  const initialDate = parseInputValue(value);
  const [includeTime, setIncludeTime] = useState(() => {
    // Prüfe ob der Wert bereits eine Uhrzeit enthält
    if (initialDate) {
      return initialDate.getHours() !== 0 || initialDate.getMinutes() !== 0;
    }
    return false;
  });
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  const [timeValue, setTimeValue] = useState(() => {
    if (initialDate) {
      return format(initialDate, 'HH:mm');
    }
    return '12:00';
  });

  // Synchronisiere mit externen Wertänderungen
  React.useEffect(() => {
    const newDate = parseInputValue(value);
    setSelectedDate(newDate);
    
    if (newDate) {
      const hasTime = newDate.getHours() !== 0 || newDate.getMinutes() !== 0;
      setIncludeTime(hasTime);
      setTimeValue(format(newDate, 'HH:mm'));
    }
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    
    if (date) {
      // Erstelle eine neue Instanz um Mutationen zu vermeiden
      const newDate = new Date(date);
      
      if (includeTime) {
        const [hours, minutes] = timeValue.split(':');
        newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      } else {
        // Setze Zeit auf Mitternacht für reine Datumsauswahl
        newDate.setHours(0, 0, 0, 0);
      }
      
      onChange(newDate.toISOString());
    } else {
      onChange(null);
    }
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
    
    if (selectedDate) {
      const [hours, minutes] = time.split(':');
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      onChange(newDate.toISOString());
    }
  };

  const toggleTimeInclude = (checked: boolean) => {
    setIncludeTime(checked);
    
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      
      if (checked) {
        // Füge die aktuelle Uhrzeit hinzu
        const [hours, minutes] = timeValue.split(':');
        newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      } else {
        // Entferne die Uhrzeit (setze auf Mitternacht)
        newDate.setHours(0, 0, 0, 0);
      }
      
      onChange(newDate.toISOString());
    }
  };

  const handleClearDate = () => {
    setSelectedDate(undefined);
    onChange(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Date Selection */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full h-6 text-xs justify-start"
            size="sm"
          >
            <CalendarIcon className="w-3 h-3 mr-2" />
            {selectedDate ? (
              format(selectedDate, includeTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy', { locale: de })
            ) : (
              'Datum wählen...'
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="p-3 space-y-2">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
              locale={de}
            />
            {selectedDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearDate}
                className="w-full h-6 text-xs"
              >
                Datum löschen
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Time Toggle and Input */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Switch
            checked={includeTime}
            onCheckedChange={toggleTimeInclude}
            className="scale-75"
            disabled={!selectedDate}
          />
          <Label className="text-xs text-gray-600">Uhrzeit</Label>
        </div>
        
        {includeTime && selectedDate && (
          <div className="flex items-center gap-1 flex-1">
            <Clock className="w-3 h-3 text-gray-400" />
            <Input
              type="time"
              value={timeValue}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="h-6 text-xs flex-1"
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Integer Input Component
 */
export const IntegerInput: React.FC<StaticInputProps> = ({ value, onChange, className }) => {
  const handleChange = (inputValue: string) => {
    const numValue = inputValue === '' ? null : parseInt(inputValue, 10);
    if (inputValue === '' || !isNaN(numValue!)) {
      onChange(numValue);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <Hash className="w-3 h-3 text-gray-400" />
        <Input
          type="number"
          step="1"
          value={value ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Ganzzahl eingeben..."
          className="h-6 text-xs flex-1"
        />
      </div>
    </div>
  );
};

/**
 * Float Input Component
 */
export const FloatInput: React.FC<StaticInputProps> = ({ value, onChange, className }) => {
  const [text, setText] = useState<string>(() => (
    value !== null && value !== undefined ? String(value).replace('.', ',') : ''
  ));
  const [isFocused, setIsFocused] = useState(false);

  React.useEffect(() => {
    if (!isFocused) {
      if (value === null || value === undefined) {
        setText('');
      } else {
        const num = typeof value === 'number' ? value : Number(value);
        if (!isNaN(num)) {
          const display = Number.isInteger(num)
            ? num.toFixed(2).replace('.', ',')
            : String(num).replace('.', ',');
          setText(display);
        } else {
          setText(String(value));
        }
      }
    }
  }, [value, isFocused]);

  const handleChange = (inputValue: string) => {
    setText(inputValue);
    const normalized = inputValue.replace(',', '.');
    const isValidPattern = /^-?\d*(?:[.,]\d*)?$/.test(inputValue);
    if (!isValidPattern) return;

    if (normalized === '') {
      onChange(null);
      return;
    }

    const isPartial = /^-?$/.test(inputValue) || /^-?[.,]$/.test(inputValue) || /^-?\d+[.,]$/.test(inputValue);
    if (isPartial) return;

    const parsed = parseFloat(normalized);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <Hash className="w-3 h-3 text-gray-400" />
        <Input
          type="text"
          inputMode="decimal"
          value={text}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            const t = text.trim();
            setIsFocused(false);
            if (t === '') {
              onChange(null);
              setText('');
              return;
            }
            const integerOnly = /^-?\d+$/.test(t);
            const endsWithSep = /^-?\d+[.,]$/.test(t);
            const hasDecimals = /^-?\d+[.,]\d+$/.test(t);
            if (integerOnly || endsWithSep) {
              const intDigits = t.replace(/[.,]$/, '');
              setText(`${intDigits},00`);
              onChange(parseFloat(intDigits.replace(',', '.')));
              return;
            }
            if (hasDecimals) {
              const normalized = t.replace(',', '.');
              setText(t.replace('.', ','));
              onChange(parseFloat(normalized));
              return;
            }
            const normalized = t.replace(',', '.');
            const parsed = parseFloat(normalized);
            if (!isNaN(parsed)) {
              const hasSep = t.includes(',') || t.includes('.');
              setText(hasSep ? t.replace('.', ',') : `${t},00`);
              onChange(parsed);
            }
          }}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Kommazahl eingeben..."
          className="h-6 text-xs flex-1"
        />
      </div>
    </div>
  );
};

/**
 * Color Input Component
 */
export const ColorInput: React.FC<StaticInputProps> = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isCustomer } = usePermissions();
  const { data: org } = useOrganizationsCustomerFindOneQuery({ enabled: isCustomer });
  
  // Vordefinierte Farbpalette
  const colorPresets = [
    '#001f3f', // Navy
    '#0074D9', // Blue
    '#2347BD', // Denim Blue
    '#7FDBFF', // Aqua
    '#39CCCC', // Teal
    '#B10DC9', // Purple
    '#F012BE', // Fuchsia
    '#85144b', // Maroon
    '#ed1c24', // Red
    '#FF851B', // Orange
    '#FFDC00', // Yellow
    '#3D9970', // Olive
    '#2ECC40', // Green
    '#29AF34', // Wageningen Green
    '#01FF70', // Lime
    '#111111', // Black
    '#AAAAAA', // Gray
    '#DDDDDD', // Silver
    '#FFFFFF', // White
  ];

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full h-6 text-xs justify-start"
            size="sm"
          >
            <div className="flex items-center gap-2 flex-1">
              <Palette className="w-3 h-3 text-gray-400" />
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: value || '#000000' }}
              />
              <span className="flex-1 text-left">
                {value || 'Farbe wählen...'}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="space-y-3">
            {/* Organization Colors */}
            {(org?.primaryColor || org?.secondaryColor) && (
              <div className="space-y-2">
                <Label className="text-xs font-medium">Vereinsfarben</Label>
                <div className="flex gap-2">
                  {org.primaryColor && (
                    <button
                      onClick={() => handleColorSelect(org.primaryColor!)}
                      className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition-transform relative shadow-sm"
                      style={{ backgroundColor: org.primaryColor }}
                      title="Primärfarbe"
                    >
                      {value === org.primaryColor && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-md" />
                      )}
                    </button>
                  )}
                  {org.secondaryColor && (
                    <button
                      onClick={() => handleColorSelect(org.secondaryColor!)}
                      className="w-8 h-8 rounded-full border border-gray-200 hover:scale-110 transition-transform relative shadow-sm"
                      style={{ backgroundColor: org.secondaryColor }}
                      title="Sekundärfarbe"
                    >
                      {value === org.secondaryColor && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow-md" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Custom Color Input */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Benutzerdefinierte Farbe</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={value || '#000000'}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-8 h-6 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={value || '#000000'}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder="#000000"
                  className="h-6 text-xs flex-1"
                />
              </div>
            </div>

            {/* Color Presets */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Farbpalette</Label>
              <div className="grid grid-cols-5 gap-1">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorSelect(color)}
                    className="w-8 h-6 rounded border border-gray-300 hover:scale-110 transition-transform relative"
                    style={{ backgroundColor: color }}
                  >
                    {value === color && (
                      <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow-sm" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * Image Input Component
 */
export const ImageInput: React.FC<StaticInputProps> = ({ value, onChange, className, allowedMimeTypes }) => {
  const { isCustomer } = usePermissions();
  const { data: org } = useOrganizationsCustomerFindOneQuery({ enabled: isCustomer });

  const displayUrl = useMemo(() => {
    // If we have a direct path (signed URL), use it
    if (value && value.path) return value.path;
    
    // Fallback: If we only have relativePath, try to find the matching signed URL from org
    if (isCustomer && value && value.relativePath && org?.logos && org?.logoUrls) {
      const index = org.logos.findIndex(l => l.path === value.relativePath);
      if (index !== -1 && org.logoUrls[index]) {
        return org.logoUrls[index];
      }
    }
    
    return null;
  }, [value, org, isCustomer]);

  const isSvg = useMemo(() => {
    if (!displayUrl) return false;
    // Check if URL ends with .svg (ignoring query params) or is a data URI for SVG
    return /\.svg($|\?)/i.test(displayUrl) || /^data:image\/svg\+xml/.test(displayUrl);
  }, [displayUrl]);

  const handleImageSelected = (path: string, id: string | null, displayName: string, width: number, height: number, previewUrl?: string, relativePath?: string) => {
    onChange({ mediaId: id || null, path: path, relativePath: relativePath || null }, { width, height });
  };

  const handleClearImage = () => {
    onChange(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {displayUrl ? (
        <div className="relative w-full h-32 rounded-md overflow-hidden border border-gray-200 group">
          <img
            src={displayUrl}
            crossOrigin="anonymous"
            alt="Vorschau"
            className={`w-full h-full ${isSvg ? 'object-contain p-2' : 'object-cover'}`}
          />
          <button
            type="button"
            onClick={handleClearImage}
            className="absolute top-1 right-1 p-1 bg-black bg-opacity-50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Bild entfernen"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center w-full h-32 rounded-md border-2 border-dashed border-gray-200 text-gray-400">
          <ImageIcon className="h-8 w-8" />
        </div>
      )}

      <AddImageDialog
        allowedMimeTypes={allowedMimeTypes}
        trigger={
          <Button
            type="button"
            variant="outline"
            className="w-full h-6 text-xs justify-center"
            size="sm"
          >
            <ImageIcon className="w-3 h-3 mr-2" />
            {value && (value.path || value.relativePath) ? 'Bild ändern' : 'Bild auswählen'}
          </Button>
        }
        onImageSelected={handleImageSelected}
      />
    </div>
  );
};

/**
 * Main Static Input Renderer
 * Wählt automatisch die richtige Komponente basierend auf dem MediaType
 */
export const StaticInputRenderer: React.FC<StaticInputProps> = ({ 
  value, 
  onChange, 
  mediaType, 
  className,
  allowedMimeTypes
}) => {
  switch (mediaType) {
    case 'text':
      return <TextInput value={value} onChange={onChange} mediaType={mediaType} className={className} />;
    
    case 'date':
      return <DateTimeInput value={value} onChange={onChange} mediaType={mediaType} className={className} />;
    
    case 'int':
      return <IntegerInput value={value} onChange={onChange} mediaType={mediaType} className={className} />;
    
    case 'float':
      return <FloatInput value={value} onChange={onChange} mediaType={mediaType} className={className} />;
    
    case 'color':
      return <ColorInput value={value} onChange={onChange} mediaType={mediaType} className={className} />;
    
    case 'image':
      return <ImageInput value={value} onChange={onChange} mediaType={mediaType} className={className} allowedMimeTypes={allowedMimeTypes} />;

    default:
      // Fallback für unbekannte Typen
      return <TextInput value={value} onChange={onChange} mediaType={mediaType} className={className} />;
  }
};